import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedSection from '@/components/ui/AnimatedSection';
import EventCard from '@/components/agenda/EventCard';
import EventFilter from '@/components/agenda/EventFilter';
import { useEvents } from '@/hooks/useEvents';

export default function Agenda() {
    const [filter, setFilter] = useState('Tous');
    const { data: allEvents, isLoading } = useEvents({ type: filter === 'Tous' ? null : filter });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingEvents = allEvents?.filter(e => new Date(e.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date)) || [];
    const pastEvents = allEvents?.filter(e => new Date(e.date) < today).sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

    return (
        <div className="min-h-screen bg-gray-50 pt-36 pb-20">
            <div className="container mx-auto px-6">

                <AnimatedSection className="text-center mb-16">
                    <h1 className="text-4xl md:text-6xl font-serif font-bold text-primary-900 mb-6">
                        L'Agenda <span className="text-accent-500">UTA</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Retrouvez ici toutes nos conférences, excursions et activités conviviales.
                        Utilisez les filtres pour affiner votre recherche.
                    </p>
                </AnimatedSection>

                <EventFilter currentFilter={filter} onFilterChange={setFilter} />

                {/* Upcoming Events */}
                <div className="mb-20">
                    <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8 flex items-center gap-4">
                        <span className="w-2 h-8 bg-accent-500 rounded-full"></span>
                        Événements à venir
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <AnimatePresence mode='popLayout'>
                            {!isLoading && upcomingEvents.map((event) => (
                                <motion.div
                                    key={event.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <EventCard event={event} showImage={true} />
                                </motion.div>
                            ))}
                        </AnimatePresence>

                        {isLoading && (
                            [1, 2, 3].map((i) => (
                                <div key={i} className="h-[450px] bg-white rounded-xl shadow-sm animate-pulse border border-gray-100" />
                            ))
                        )}

                        {!isLoading && upcomingEvents.length === 0 && (
                            <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-100 border-dashed">
                                Aucun événement à venir pour ce filtre.
                            </div>
                        )}
                    </div>
                </div>

                {/* Past Events */}
                <div>
                    <h2 className="text-2xl font-serif font-bold text-gray-500 mb-8 flex items-center gap-4">
                        <span className="w-2 h-8 bg-gray-300 rounded-full"></span>
                        Événements antérieurs
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 opacity-75 grayscale-[30%] hover:grayscale-0 transition-all duration-500">
                        {!isLoading && pastEvents.map((event) => (
                            <EventCard key={event.id} event={event} showImage={false} />
                        ))}
                    </div>

                    {!isLoading && pastEvents.length === 0 && (
                        <div className="text-center py-8 text-gray-400 italic">
                            Aucun événement passé.
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
